import { Grid, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  certificaattypeLijst,
  functietitelLijst,
  talentpoolLijst,
  useLocatieMutaties,
  useReferentielijst,
  useReferentieMutaties,
  vaardigheidLijst,
} from '../../api/referentie.ts';
import type { Certificaattype, Functietitel, Locatie, Talentpool, Vaardigheid } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { TalentpoolVeld } from '../../componenten/keuzevelden.tsx';
import { Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull, naarGetal, naarTekst } from '../../helpers/formulier.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

export interface FormulierProps<T> {
  open: boolean;
  onSluit: () => void;
  bestaand?: T | undefined;
}

function synoniemenNaarLijst(tekst: string): string[] {
  return tekst
    .split(/[,\n;]/)
    .map((deel) => deel.trim())
    .filter(Boolean);
}

/**
 * Voegt een talentpool toe of wijzigt hem.
 * @param props De bestaande talentpool om te wijzigen.
 * @returns De dialoog.
 */
export function TalentpoolFormulier({ open, onSluit, bestaand }: FormulierProps<Talentpool>) {
  const mutaties = useReferentieMutaties(talentpoolLijst);
  const begin = () => ({ naam: bestaand?.naam ?? '', omschrijving: bestaand?.omschrijving ?? '' });
  const formulier = useForm({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = { naam: waarden.naam, omschrijving: leegNaarNull(waarden.omschrijving) };
      return bestaand ? mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer }) : mutaties.maak.mutateAsync(invoer);
    },
    { melding: 'Talentpool opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Talentpool wijzigen' : 'Nieuwe talentpool'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          <Tekstveld naam="omschrijving" label="Omschrijving" meerregelig rijen={3} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

interface FunctietitelWaarden {
  naam: string;
  synoniemen: string;
  standaardTalentpool: Talentpool | null;
}

/**
 * Voegt een functietitel toe of wijzigt hem, met synoniemen en de talentpool waar hij standaard in valt.
 * @param props De bestaande functietitel om te wijzigen.
 * @returns De dialoog.
 */
export function FunctietitelFormulier({ open, onSluit, bestaand }: FormulierProps<Functietitel>) {
  const mutaties = useReferentieMutaties(functietitelLijst);
  const talentpools = useReferentielijst(talentpoolLijst);
  const begin = (): FunctietitelWaarden => ({
    naam: bestaand?.naam ?? '',
    synoniemen: bestaand?.synoniemen.join(', ') ?? '',
    standaardTalentpool: talentpools.data?.find((pool) => pool.id === bestaand?.standaardTalentpoolId) ?? null,
  });
  const formulier = useForm<FunctietitelWaarden>({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, talentpools.data, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = {
        naam: waarden.naam,
        synoniemen: synoniemenNaarLijst(waarden.synoniemen),
        standaardTalentpoolId: waarden.standaardTalentpool?.id ?? null,
      };
      return bestaand ? mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer }) : mutaties.maak.mutateAsync(invoer);
    },
    { melding: 'Functietitel opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Functietitel wijzigen' : 'Nieuwe functietitel'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Stack spacing={2}>
          <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          <Tekstveld naam="synoniemen" label="Synoniemen" hulptekst="Gescheiden door komma's" meerregelig rijen={2} />
          <TalentpoolVeld naam="standaardTalentpool" label="Valt standaard in talentpool" />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

/**
 * Voegt een vaardigheid toe of wijzigt haar, met synoniemen.
 * @param props De bestaande vaardigheid om te wijzigen.
 * @returns De dialoog.
 */
export function VaardigheidFormulier({ open, onSluit, bestaand }: FormulierProps<Vaardigheid>) {
  const mutaties = useReferentieMutaties(vaardigheidLijst);
  const begin = () => ({ naam: bestaand?.naam ?? '', synoniemen: bestaand?.synoniemen.join(', ') ?? '' });
  const formulier = useForm({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = { naam: waarden.naam, synoniemen: synoniemenNaarLijst(waarden.synoniemen) };
      return bestaand ? mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer }) : mutaties.maak.mutateAsync(invoer);
    },
    { melding: 'Vaardigheid opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Vaardigheid wijzigen' : 'Nieuwe vaardigheid'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          <Tekstveld naam="synoniemen" label="Synoniemen" hulptekst="Gescheiden door komma's" meerregelig rijen={2} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

/**
 * Voegt een certificaattype toe of wijzigt het.
 * @param props Het bestaande certificaattype om te wijzigen.
 * @returns De dialoog.
 */
export function CertificaattypeFormulier({ open, onSluit, bestaand }: FormulierProps<Certificaattype>) {
  const mutaties = useReferentieMutaties(certificaattypeLijst);
  const begin = () => ({
    naam: bestaand?.naam ?? '',
    uitgever: bestaand?.uitgever ?? '',
    geldigheidMaanden: naarTekst(bestaand?.geldigheidMaanden),
  });
  const formulier = useForm({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = {
        naam: waarden.naam,
        uitgever: leegNaarNull(waarden.uitgever),
        geldigheidMaanden: naarGetal(waarden.geldigheidMaanden),
      };
      return bestaand ? mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer }) : mutaties.maak.mutateAsync(invoer);
    },
    { melding: 'Certificaattype opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Certificaattype wijzigen' : 'Nieuw certificaattype'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          <Tekstveld naam="uitgever" label="Uitgever" />
          <Tekstveld
            naam="geldigheidMaanden"
            label="Geldigheid in maanden"
            type="number"
            min={1}
            max={600}
            hulptekst="Leeg: onbeperkt geldig"
          />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

/**
 * Wijzigt een locatie.
 * @param props De locatie.
 * @returns De dialoog.
 */
export function LocatieFormulier({ open, onSluit, bestaand }: FormulierProps<Locatie> & { bestaand: Locatie }) {
  const mutaties = useLocatieMutaties();
  const begin = () => ({
    postcode: bestaand.postcode,
    plaats: bestaand.plaats,
    provincie: bestaand.provincie ?? '',
    lat: naarTekst(bestaand.lat),
    lon: naarTekst(bestaand.lon),
  });
  const formulier = useForm({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) =>
      mutaties.wijzig.mutateAsync({
        id: bestaand.id,
        invoer: {
          postcode: waarden.postcode,
          plaats: waarden.plaats,
          provincie: leegNaarNull(waarden.provincie),
          lat: naarGetal(waarden.lat),
          lon: naarGetal(waarden.lon),
        },
      }),
    { melding: 'Locatie opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Locatie wijzigen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <Tekstveld naam="postcode" label="Postcode" verplicht autoFocus />
          </Grid>
          <Grid size={6}>
            <Tekstveld naam="plaats" label="Plaats" verplicht />
          </Grid>
          <Grid size={12}>
            <Tekstveld naam="provincie" label="Provincie" />
          </Grid>
          <Grid size={6}>
            <Tekstveld naam="lat" label="Breedtegraad" type="number" stap="any" />
          </Grid>
          <Grid size={6}>
            <Tekstveld naam="lon" label="Lengtegraad" type="number" stap="any" />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
